import { useCallback, useRef, useEffect } from 'react';
import { ErrorBoundaryProps, ErrorSeverity } from '@/components/ErrorBoundary';
import { ErrorLogger } from '@/components/ErrorBoundary/ErrorLogger';
import { useNotification } from '@/components/ErrorBoundary/NotificationCenter';

export interface ErrorHandlerOptions {
  context?: string;
  severity?: ErrorSeverity;
  enableNotifications?: boolean;
  enableLogging?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

interface RetryState {
  attempts: number;
  lastAttempt: number;
  isRetrying: boolean;
}

export interface ErrorHandlerResult {
  handleError: (error: Error, context?: string) => string;
  handleAsyncError: <T>(
    asyncFn: () => Promise<T>,
    context?: string
  ) => Promise<T | null>;
  retryWithBackoff: <T>(
    fn: () => Promise<T>,
    maxAttempts?: number,
    baseDelay?: number
  ) => Promise<T>;
  getErrorStats: () => {
    totalErrors: number;
    errorsByContext: Record<string, number>;
    errorsBySeverity: Record<ErrorSeverity, number>;
  };
  clearErrors: () => void;
  retryState: Record<string, RetryState>;
}

export const useErrorHandler = (options: ErrorHandlerOptions = {}): ErrorHandlerResult => {
  const {
    context: defaultContext,
    severity: defaultSeverity = 'medium',
    enableNotifications = true,
    enableLogging = true,
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  const errorLogger = ErrorLogger.getInstance();
  const { showNotification } = useNotification();
  const retryStates = useRef<Record<string, RetryState>>({});

  const handleError = useCallback((
    error: Error,
    context?: string
  ): string => {
    const errorContext = context || defaultContext || 'Unknown';
    const errorId = errorLogger.logError(
      error,
      undefined,
      defaultSeverity,
      errorContext
    );

    // Show user-friendly notification
    if (enableNotifications) {
      const notification = getUserFriendlyNotification(error, errorContext, defaultSeverity);
      showNotification(notification);
    }

    return errorId;
  }, [defaultContext, defaultSeverity, enableNotifications, showNotification, errorLogger]);

  const handleAsyncError = useCallback(async <T extends any>(
    asyncFn: () => Promise<T>,
    context?: string
  ): Promise<T | null> => {
    try {
      return await asyncFn();
    } catch (error) {
      const errorId = handleError(error as Error, context);

      // Log additional details for async errors
      if (enableLogging) {
        console.error(`Async error in ${context || defaultContext}:`, {
          errorId,
          error: error as Error,
          timestamp: new Date().toISOString()
        });
      }

      return null;
    }
  }, [handleError, defaultContext, enableLogging]);

  const retryWithBackoff = useCallback(
    async (
      fn: () => Promise<any>,
      maxAttempts?: number,
      baseDelay?: number
    ): Promise<any> => {
      const maxAtt = maxAttempts || maxRetries;
      const delay = baseDelay || retryDelay;
      const fnKey = fn.name || 'anonymous';
      let lastError: Error;

      for (let attempt = 1; attempt <= maxAtt; attempt++) {
        try {
          // Update retry state
          retryStates.current[fnKey] = {
            attempts: attempt,
            lastAttempt: Date.now(),
            isRetrying: true
          };

          const result = await fn();

          // Reset retry state on success
          delete retryStates.current[fnKey];

          return result;
        } catch (error) {
          lastError = error as Error;

          if (attempt === maxAtt) {
            // Final attempt failed
            retryStates.current[fnKey] = {
              attempts: maxAtt,
              lastAttempt: Date.now(),
              isRetrying: false
            };

            handleError(lastError, `Retry failed after ${maxAtt} attempts`);
            throw lastError;
          }

          // Calculate exponential backoff delay
          const retryDelay = delay * Math.pow(2, attempt - 1);

          // Show retry notification
          if (enableNotifications && attempt > 1) {
            showNotification({
              type: 'warning',
              title: 'Tentando novamente',
              message: `Falha na tentativa ${attempt} de ${maxAtt}. Tentando novamente em ${Math.round(retryDelay / 1000)}s...`,
              duration: retryDelay
            });
          }

          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      // This should never be reached
      throw lastError!;
    },
    [maxRetries, retryDelay, handleError, enableNotifications, showNotification]
  );

  const getErrorStats = useCallback(() => {
    const stats = errorLogger.getErrorStats();

    // Add retry statistics
    const retryStats = Object.values(retryStates.current).reduce(
      (acc, state) => ({
        totalRetries: acc.totalRetries + state.attempts,
        activeRetries: acc.activeRetries + (state.isRetrying ? 1 : 0)
      }),
      { totalRetries: 0, activeRetries: 0 }
    );

    return {
      ...stats,
      ...retryStats
    };
  }, [errorLogger]);

  const clearErrors = useCallback(() => {
    errorLogger.clearQueue();
    retryStates.current = {};
  }, [errorLogger]);

  // Cleanup old retry states
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      const fiveMinutesAgo = now - 5 * 60 * 1000;

      Object.keys(retryStates.current).forEach(key => {
        const state = retryStates.current[key];
        if (!state.isRetrying && state.lastAttempt < fiveMinutesAgo) {
          delete retryStates.current[key];
        }
      });
    }, 60000); // Check every minute

    return () => clearInterval(cleanup);
  }, []);

  return {
    handleError,
    handleAsyncError,
    retryWithBackoff,
    getErrorStats,
    clearErrors,
    retryState: retryStates.current
  };
};

// Helper function to create user-friendly notifications
function getUserFriendlyNotification(
  error: Error,
  context: string,
  severity: ErrorSeverity
) {
  const errorMessages: Record<string, { title: string; message: string }> = {
    'Network Error': {
      title: 'Erro de conexão',
      message: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.'
    },
    'Supabase Error': {
      title: 'Erro no banco de dados',
      message: 'Ocorreu um erro ao acessar os dados. Tente novamente em alguns instantes.'
    },
    'Avatar Error': {
      title: 'Erro na foto de perfil',
      message: 'Não foi possível carregar sua foto de perfil. Usando imagem padrão.'
    },
    'State Sync Error': {
      title: 'Erro de sincronização',
      message: 'Os dados podem não estar atualizados. Recarregue a página.'
    },
    'React Error': {
      title: 'Erro na aplicação',
      message: 'Ocorreu um erro inesperado. Nossa equipe foi notificada.'
    }
  };

  const defaultMessage = {
    title: 'Erro inesperado',
    message: 'Ocorreu um erro. Tente novamente ou recarregue a página.'
  };

  const notification = errorMessages[context] || defaultMessage;

  return {
    type: severity === 'critical' || severity === 'high' ? 'error' : 'warning',
    ...notification,
    duration: severity === 'critical' ? 0 : 5000, // Critical errors don't auto-dismiss
    action: {
      label: 'Recarregar página',
      onClick: () => window.location.reload()
    }
  };
}

// Hook specifically for Supabase errors
export const useSupabaseErrorHandler = () => {
  const { handleError } = useErrorHandler({
    context: 'Supabase Error',
    severity: 'high',
    enableNotifications: true
  });

  const handleSupabaseError = useCallback((error: any, operation: string) => {
    // Extract meaningful information from Supabase errors
    let errorMessage = 'Erro desconhecido do Supabase';
    let context = `Supabase: ${operation}`;

    if (error?.message) {
      errorMessage = error.message;
    }

    if (error?.code) {
      context += ` (Código: ${error.code})`;
    }

    const enhancedError = new Error(errorMessage);
    enhancedError.stack = error?.stack;

    return handleError(enhancedError, context);
  }, [handleError]);

  return { handleSupabaseError };
};

// Hook for state synchronization errors
export const useStateSyncErrorHandler = () => {
  const { handleError, retryWithBackoff } = useErrorHandler({
    context: 'State Sync Error',
    severity: 'medium',
    enableNotifications: true
  });

  const handleStateSyncError = useCallback((
    error: Error,
    stateKey: string,
    retryFn?: () => Promise<void>
  ) => {
    const context = `State Sync: ${stateKey}`;
    const errorId = handleError(error, context);

    if (retryFn) {
      // Attempt to retry the state synchronization
      retryWithBackoff(retryFn, 3, 1000).catch(retryError => {
        console.error(`Failed to retry state sync for ${stateKey}:`, retryError);
      });
    }

    return errorId;
  }, [handleError, retryWithBackoff]);

  return { handleStateSyncError };
};